import { Translation, TranslationCollection } from "./translation";

export interface ProductPage {
    product: ProductModel;
    translations: TranslationModel;
}

export interface ProductModel {
    showPrice: boolean;
    variants: Variant[];
    variantFilterOptions: VariantFilterOptionsModel[];
    selectedVariantId: string;
    productMaterialConsumption: number;
    consumptionArea: number;
    consumptionLayer: number;
    quantity: number;
    quantityDefault: number;
    palletRequired: number;
    priceInfoText: string;
    checkoutUrl: string;
    castQuantityCalculatorCubeImageUrl: string;
    castQuantityCalculatorCylinderImageUrl: string;
    castQuantityCalculatorTriangularImageUrl: string;
    isMobile: boolean;
    mortarCalculatorViewModel: MortarCalculatorModel;
    buildingOptionFromRequest: string;
    itemPriceUnit: string;
    itemSecondaryPackagingUnit: string;
    isOnlyShoppingList: boolean;
}

export interface MortarCalculatorModel {
    mortarProductsList: ApplicableProducts[];
    buildingOptions: ApplicableFilterOption[];
}

export interface ApplicableProducts {
    productId: string;
    buildingOption: string;
    variantsCanBeCalculated: Variant[];
    productsCannotBeCalculated: ProductsCannotBeCalculated[];
}

export interface ProductsCannotBeCalculated {
    baseProductId: string;
    baseProductName: string;
    url: string;
}

export interface ApplicableFilterOption {
    id: string;
    name: string;
}

export interface VariantFilterOptionsModel {
    filterType: FilterType;
    filterId: string;
    filterName: string;
    filterValues: VariantFilterValue[];
    filterValueUnit: string;
}

export enum FilterType {
    Text = 0,
    Image = 1
}

export interface VariantFilterValue {
    variantId: string;
    value: string;
    imageResourceUrl: string;
}

export interface Variant {
    variantId: string;
    variantSystemId: string;
    variantName: string;
    category: string;
    url: string;
    productFacts: ProductFact[];
    price: string;
    priceValue: number;
    currencyCode: string;
    showBuyButton: boolean;
    showQuantity: boolean;
    showPalletQuantity: boolean;
    variantAttributes: VariantAttributeModel[];
    itemPrimaryPackagingWeight: number;
    itemSecondaryPackagingContent: number;
    isInFavouriteList: boolean;
    relatedProducts: RelatedProductModel[];
    itemMaterialConsumption: number;
    itemConsumptionCalculatorBlock: boolean;
    itemConsumptionCalculator: string;
    itemConsumptionCalculatorCast: boolean;
    itemConsumptionCalculatorBag: boolean;
    baseProductArticleNumber: string;
    itemConsumptionCalculatorMortar: boolean;
    itemConsumptionStringJoint: number;
    itemConsumptionFullJoint: number;
    itemConsumptionCalculatorPlaster: boolean;
    itemConsumptionCalculatorSandBoxSand: boolean;
    itemConsumptionCalculatorFogSand: boolean;
    itemConsumptionCalculatorUBlock: boolean;
    insulationVariantModel: InsulationVariantModel;
    itemFillingWithInsulation: number;
    itemAdditionalMaterialConsumption: number;
    fillingProductModel: FillingProductModel[];
    carbonSavingsModel: CarbonSavingsModel;
    isBuyable: boolean;
    fogSandMultiplyingFactor: number;
}

export interface InsulationVariantModel {
    id: string;
    name: string;
    url: string;
    packagingLength: number;
    variantSystemId: string;
}

export interface FillingProductModel {
    baseProductId: string;
    name: string;
    url: string;
    fillingVariantModel: FillingVariantModel[];
}

export interface FillingVariantModel {
    id: string;
    packagingWeight: number;
    variantSystemId: string;
}

export interface VariantAttributeModel {
    attributeId: string;
    attributeName: string;
    attributeValue: string;
}

export interface TranslationModel extends TranslationCollection {
    buyButton: Translation;
    variantName: Translation;
    variantArticleNumber: Translation;
    variantSelectorLabel: Translation;
    consumptionCalculatorHeader: Translation;
    consumptionCalculatorDisclaimer: Translation;
    consumptionCalculatorHeader1: Translation;
    consumptionCalculatorHeader2: Translation;
    consumptionCalculatorUnit1: Translation;
    consumptionCalculatorUnit2: Translation;
    itemSecondaryPackagingContentLabel: Translation;
    palletQunatityLabel: Translation;
    itemQuantityLabel: Translation;
    purchaseInfoText: Translation;
    relatedProductsHeader: Translation;
}

export interface ProductFact {
    name: string;
    value: string;
}

export interface RelatedProductModel {
    brand: string;
    description: string;
    descriptionShort: string;
    id: string;
    systemId: string;
    image: string;
    isInStock: boolean;
    name: string;
    showPrice: boolean;
    price: string;
    priceValue: number;
    campaignPrice: string;
    showBuyButton: boolean;
    showQuantityField: boolean;
    url: string;
    quantity: number;
    showPackagingAmount: boolean;
    unitOptions: UnitOptions[];
    isGDS: boolean;
    hasCampaignPrice: boolean;
    campaignDisplay: CampaignDisplay;
    hasPrice: ConstrainBooleanParameters;
    minimalVariantPrice: number;
    quantityFieldId: string;
    isProductSelectorPageType: boolean;
    variantsHaveDifferentPrices: boolean;
    hasMultipleVariants: boolean;
    quantitySelectorPlaceholderClass: string;
    currencyCode: string;
    buyButtonTitle: string;
    buyButtonClass: string;
    isVariant: boolean;
    dataCartTag: string;
    itemSecondaryPackagingContent: number;
}

export interface CampaignDisplay {
    image: string;
    text: string;
    textStyle: string;
}

export interface UnitOptions {
    packagingName: string;
    packagingUnit: string;
    packagingQuantity: number;
}

export interface CarbonSavingsModel {
    isCurrentVariantECO: boolean;
    mainVariantName: string;
    ecoVariantName: string;
    globalWarmingPotentialMainProduct: number;
    globalWarmingPotentialECOProduct: number;
    savingsComparedToNonECOVariant: number;
    ecoVariantUrl: string;
    mainVariantUrl: string;
    thisIsHowWeCalculatedPageUrl: string;
    ecoVariantId: string;
    currentVariantId: string;
    packagingFactor: number;
    imageUrl: string;
}

export interface ProductEnvironmentDatabase {
    displayName: string;
    url: string;
    iconUrl: string;
    productEnvironmentDatabaseLevel2: ProductEnvironmentDatabaseLevel2;
    aboutText: string;
    isIndicatorSplit: boolean;
    imageUrl: string;
    modalEnabled: boolean;
}

export interface ProductEnvironmentDatabaseLevel2 {
    criteriaName: string;
    activatedCriteria: string;
    indicatorCriteras: string[];
    allAvailableOptions: EnvironmentDatabaseLevl2Option[];
}

export interface EnvironmentDatabaseLevl2Option {
    id: string;
    name: string;
    indicatorVersion: string;
    indicatorIdentifier: string;
    indicatorValue: string;
}

